import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MatchPitchGraphics } from './MatchPitchGraphics';
import { MatchEvents } from './MatchEvents';
import { SubstitutionModal } from './SubstitutionModal';
import { quickSimulate, makeSubstitution, simulateExtraTime, simulatePenaltyShootout, generateMatchRatings } from '../utils/simulationEngine';
import { generateCommentary } from '../utils/commentary';
import { FORMATIONS } from '../utils/gameData';
import { Play, Pause, Lightning, ArrowsLeftRight, Strategy, X, Timer, Trophy } from '@phosphor-icons/react';

/**
 * Shared match sim modal with:
 *  - Quick Sim
 *  - Full Sim (with pause/resume, pause-for-subs, auto-sub on injury, animated players)
 *  - Extra time + penalty shootout when `knockout` prop set and match tied on aggregate
 */
export const MatchSimModal = ({
  isOpen, onClose,
  homeTeam: initialHome, awayTeam: initialAway,
  competitionLabel = 'Match',
  onResult,
  knockout = false,
  aggregateHomeExtra = 0,
  aggregateAwayExtra = 0
}) => {
  const [home, setHome] = useState(initialHome);
  const [away, setAway] = useState(initialAway);
  const [mode, setMode] = useState('setup'); // setup | live | finished
  const [simMode, setSimMode] = useState(null); // quick | full
  const [events, setEvents] = useState([]);
  const [currentMinute, setCurrentMinute] = useState(0);
  const [speed, setSpeed] = useState([350]); // slower default
  const [paused, setPaused] = useState(false);
  const [showSub, setShowSub] = useState(false);
  const [subTeam, setSubTeam] = useState(null);
  const [subsUsed, setSubsUsed] = useState({ home: 0, away: 0 });
  const [matchRatings, setMatchRatings] = useState({});
  const [finalResult, setFinalResult] = useState(null);
  const stateRef = useRef({ eventsList: [], index: 0, paused: false, home: null, away: null, homeScore: 0, awayScore: 0 });
  const timerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setHome(initialHome);
      setAway(initialAway);
      setMode('setup');
      setSimMode(null);
      setEvents([]);
      setCurrentMinute(0);
      setPaused(false);
      setSubsUsed({ home: 0, away: 0 });
      setMatchRatings({});
      setFinalResult(null);
    }
  }, [isOpen, initialHome, initialAway]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  // Find healthiest available bench player to replace someone out
  const findSubReplacement = (team, outNumber) => {
    const outPlayer = team.players.find(p => p.number === outNumber);
    if (!outPlayer) return null;
    // Prefer same position, then any bench, excluding injured
    const benchPlayers = team.bench
      .map(n => team.players.find(p => p.number === n))
      .filter(p => p && (!p.injuredWeeks || p.injuredWeeks <= 0));
    if (benchPlayers.length === 0) return null;
    const samePos = benchPlayers.find(p => p.position === outPlayer.position);
    if (samePos) return samePos.number;
    benchPlayers.sort((a, b) => b.overall - a.overall);
    return benchPlayers[0].number;
  };

  const autoSubInjury = (injuryEvent) => {
    const side = injuryEvent.team;
    const team = side === 'home' ? stateRef.current.home : stateRef.current.away;
    if (!team) return;
    const subsCurrent = side === 'home' ? stateRef.current.homeSubs || 0 : stateRef.current.awaySubs || 0;
    if (subsCurrent >= 5) return; // no subs left (allowed up to 5 with injuries)
    const outNum = injuryEvent.player?.number;
    if (!outNum) return;
    const inNum = findSubReplacement(team, outNum);
    if (!inNum) return;
    const updated = makeSubstitution(team, outNum, inNum);
    if (side === 'home') {
      stateRef.current.home = updated;
      setHome(updated);
      stateRef.current.homeSubs = subsCurrent + 1;
      setSubsUsed(prev => ({ ...prev, home: prev.home + 1 }));
    } else {
      stateRef.current.away = updated;
      setAway(updated);
      stateRef.current.awaySubs = subsCurrent + 1;
      setSubsUsed(prev => ({ ...prev, away: prev.away + 1 }));
    }
    const playerOut = team.players.find(p => p.number === outNum);
    const playerIn = team.players.find(p => p.number === inNum);
    // Insert auto-sub event right after injury
    const autoEv = {
      type: 'substitution',
      minute: injuryEvent.minute,
      team: side,
      teamName: team.name,
      playerOut, playerIn,
      auto: true,
      commentary: `${team.name} auto-sub: ${playerIn?.name} replaces injured ${playerOut?.name}`
    };
    setEvents(prev => [...prev, autoEv]);
  };

  // Full sim loop - manages its own state via refs for pause/resume
  const runFullSimLoop = () => {
    const tick = () => {
      const st = stateRef.current;
      if (st.paused) {
        timerRef.current = setTimeout(tick, 120);
        return;
      }
      if (st.index >= st.eventsList.length) {
        // Done - check if knockout + tied, then extra time + pens
        const finalHome = st.homeScore;
        const finalAway = st.awayScore;
        const homeAgg = finalHome + aggregateHomeExtra;
        const awayAgg = finalAway + aggregateAwayExtra;
        if (knockout && homeAgg === awayAgg) {
          // Enter extra time
          const etResult = simulateExtraTime(st.home, st.away, finalHome, finalAway);
          // Append events, resume loop
          st.eventsList = [...st.eventsList, ...etResult.events];
          // Don't stop: just keep iterating
          st.etHomeScore = etResult.homeScore;
          st.etAwayScore = etResult.awayScore;
          st.etDone = true;
          timerRef.current = setTimeout(tick, speed[0]);
          return;
        }
        // If we've already done ET check
        if (knockout && st.etDone) {
          const homeAggET = st.etHomeScore + aggregateHomeExtra;
          const awayAggET = st.etAwayScore + aggregateAwayExtra;
          if (homeAggET === awayAggET && !st.pensDone) {
            const pens = simulatePenaltyShootout(st.home, st.away);
            st.eventsList = [...st.eventsList, ...pens.events];
            st.pens = pens;
            st.pensDone = true;
            timerRef.current = setTimeout(tick, speed[0]);
            return;
          }
        }
        finishMatch();
        return;
      }
      const ev = st.eventsList[st.index];
      if (ev.type === 'minute') {
        setCurrentMinute(ev.minute);
      } else {
        setEvents(prev => [...prev, ev]);
        if (ev.type === 'goal') {
          if (ev.team === 'home') st.homeScore += 1; else st.awayScore += 1;
        }
        // Live rating refresh on every meaningful event so "Show ratings"
        // reflects what's happened so far (goals, cards, saves, assists).
        if (['goal', 'save', 'yellow_card', 'red_card', 'injury'].includes(ev.type)) {
          try {
            const eventsSoFar = st.eventsList.slice(0, st.index + 1);
            setMatchRatings(generateMatchRatings(st.home, st.away, eventsSoFar));
          } catch (_) { /* ignore */ }
        }
        // Auto-sub on injury
        if (ev.type === 'injury') {
          setTimeout(() => autoSubInjury(ev), 10);
        }
      }
      st.index++;
      timerRef.current = setTimeout(tick, ev.type === 'minute' ? Math.max(30, speed[0] / 3) : speed[0]);
    };
    tick();
  };

  const finishMatch = () => {
    const st = stateRef.current;
    // Compute final result
    let result = {
      homeScore: st.etDone ? st.etHomeScore : st.homeScore,
      awayScore: st.etDone ? st.etAwayScore : st.homeScore,
      events: st.eventsList,
      matchRatings: st.matchRatings || {}
    };
    // Recalculate away score correctly
    result.awayScore = st.etDone ? st.etAwayScore : st.awayScore;
    if (st.pens) {
      result.pens = { home: st.pens.homePens, away: st.pens.awayPens, winner: st.pens.winner };
    }
    // Include injuries from the events
    result.injuries = st.eventsList.filter(e => e.type === 'injury');
    setCurrentMinute(st.pens ? 120 : st.etDone ? 120 : 90);
    setMatchRatings(result.matchRatings);
    setMode('finished');
    setFinalResult(result);
    onResult?.(result, st.home, st.away);
  };

  const handleQuick = () => {
    setSimMode('quick');
    setMode('live');
    setTimeout(() => {
      let result = quickSimulate(home, away);
      let finalResult = { ...result };
      // Knockout check
      if (knockout) {
        const homeAgg = result.homeScore + aggregateHomeExtra;
        const awayAgg = result.awayScore + aggregateAwayExtra;
        if (homeAgg === awayAgg) {
          const et = simulateExtraTime(home, away, result.homeScore, result.awayScore);
          finalResult.events = [...result.events, ...et.events];
          finalResult.homeScore = et.homeScore;
          finalResult.awayScore = et.awayScore;
          if (et.homeScore + aggregateHomeExtra === et.awayScore + aggregateAwayExtra) {
            const pens = simulatePenaltyShootout(home, away);
            finalResult.events = [...finalResult.events, ...pens.events];
            finalResult.pens = { home: pens.homePens, away: pens.awayPens, winner: pens.winner };
          }
        }
      }
      setEvents(finalResult.events);
      setCurrentMinute(finalResult.pens ? 120 : 90);
      setMatchRatings(finalResult.matchRatings || {});
      setMode('finished');
      setFinalResult(finalResult);
      onResult?.(finalResult, home, away);
    }, 200);
  };

  const handleFull = () => {
    setSimMode('full');
    setMode('live');
    setEvents([]);
    setCurrentMinute(0);
    // Generate entire match up-front
    const result = quickSimulate(home, away);
    // Seed an initial baseline rating set (just the kickoff event) so the
    // "Show ratings" plates aren't blank before the first meaningful action.
    try {
      const seed = [{ type: 'kickoff', minute: 0 }];
      setMatchRatings(generateMatchRatings(home, away, seed));
    } catch (_) { /* ignore */ }
    // Convert events into minute-ticks + events stream
    const stream = [];
    let prevMinute = 0;
    result.events.forEach(ev => {
      const evMin = ev.minute != null ? ev.minute : prevMinute;
      while (prevMinute < evMin) {
        prevMinute++;
        stream.push({ type: 'minute', minute: prevMinute });
      }
      stream.push(ev);
    });
    // fill to 90
    while (prevMinute < 90) { prevMinute++; stream.push({ type: 'minute', minute: prevMinute }); }
    stateRef.current = {
      eventsList: stream,
      index: 0,
      paused: false,
      home,
      away,
      homeScore: 0,
      awayScore: 0,
      matchRatings: result.matchRatings || {},
      homeSubs: 0,
      awaySubs: 0,
      etDone: false,
      etHomeScore: 0,
      etAwayScore: 0,
      pensDone: false,
      pens: null
    };
    runFullSimLoop();
  };

  const togglePause = () => {
    stateRef.current.paused = !stateRef.current.paused;
    setPaused(stateRef.current.paused);
  };

  const handleSub = (outNum, inNum) => {
    if (!subTeam) return;
    const isHome = subTeam.id === home.id;
    const updated = makeSubstitution(subTeam, outNum, inNum);
    if (isHome) { setHome(updated); stateRef.current.home = updated; }
    else { setAway(updated); stateRef.current.away = updated; }
    setSubsUsed(prev => ({ ...prev, [isHome ? 'home' : 'away']: prev[isHome ? 'home' : 'away'] + 1 }));
    if (isHome) stateRef.current.homeSubs = (stateRef.current.homeSubs || 0) + 1;
    else stateRef.current.awaySubs = (stateRef.current.awaySubs || 0) + 1;
    const playerOut = subTeam.players.find(p => p.number === outNum);
    const playerIn = subTeam.players.find(p => p.number === inNum);
    setEvents(prev => [...prev, { type: 'substitution', minute: currentMinute, team: isHome ? 'home' : 'away', teamName: subTeam.name, playerOut, playerIn, commentary: `${subTeam.name} substitution: ${playerIn?.name} on for ${playerOut?.name}` }]);
  };

  const handleFormation = (teamId, newFormation) => {
    if (home.id === teamId) { setHome({ ...home, formation: newFormation }); if (stateRef.current.home) stateRef.current.home = { ...stateRef.current.home, formation: newFormation }; }
    if (away.id === teamId) { setAway({ ...away, formation: newFormation }); if (stateRef.current.away) stateRef.current.away = { ...stateRef.current.away, formation: newFormation }; }
  };

  const homeScore = events.filter(e => e.type === 'goal' && e.team === 'home').length;
  const awayScore = events.filter(e => e.type === 'goal' && e.team === 'away').length;
  const penInfo = finalResult?.pens;

  if (!home || !away) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#121216] border-[#2C2C35] max-w-5xl p-0 max-h-[90vh] overflow-y-auto" data-testid="match-sim-modal">
        <div className="px-5 py-3 border-b border-[#2C2C35] flex items-center justify-between sticky top-0 bg-[#121216] z-10">
          <div>
            <p className="font-heading font-bold text-white uppercase text-sm">{competitionLabel}{knockout ? ' • Knockout' : ''}</p>
            <p className="text-xs text-[#A0A0A5]">{home.name} vs {away.name}</p>
          </div>
          <button onClick={onClose} className="text-[#A0A0A5] hover:text-white" data-testid="close-sim-modal"><X size={18} /></button>
        </div>

        {mode === 'setup' && (
          <div className="p-6 space-y-5">
            <div className="flex items-center justify-center gap-6">
              <div className="text-center flex-1">
                <p className="font-heading font-bold text-white text-xl">{home.name}</p>
                <p className="text-xs text-[#6E6E73]">{home.formation}</p>
              </div>
              <span className="font-heading text-2xl text-[#6E6E73]">VS</span>
              <div className="text-center flex-1">
                <p className="font-heading font-bold text-white text-xl">{away.name}</p>
                <p className="text-xs text-[#6E6E73]">{away.formation}</p>
              </div>
            </div>
            <div className="card-elevated rounded-sm p-4 space-y-3">
              <div>
                <label className="text-xs font-bold uppercase text-[#6E6E73] tracking-wider mb-2 block">Full Sim Speed (lower = slower)</label>
                <div className="flex items-center gap-3">
                  <Timer size={16} className="text-[#6E6E73]" />
                  <Slider value={speed} onValueChange={setSpeed} min={150} max={800} step={25} className="flex-1" data-testid="sim-speed-slider" />
                  <span className="font-mono text-sm text-[#A0A0A5] w-16">{speed[0]}ms</span>
                </div>
                <p className="text-[10px] text-[#6E6E73] mt-1">{knockout ? 'If tied on aggregate → extra time → penalties' : '90 min regulation'}</p>
              </div>
              <div className="flex gap-3">
                <Button data-testid="sim-modal-quick" onClick={handleQuick} className="flex-1 bg-[#FF3B30] hover:bg-[#FF5C53] text-white">
                  <Lightning size={18} className="mr-2" weight="fill" /> Quick Sim
                </Button>
                <Button data-testid="sim-modal-full" onClick={handleFull} className="flex-1 bg-[#007AFF] hover:bg-[#0A84FF] text-white">
                  <Play size={18} className="mr-2" weight="fill" /> Full Sim (pausable)
                </Button>
              </div>
            </div>
          </div>
        )}

        {(mode === 'live' || mode === 'finished') && simMode === 'full' && (
          <div className="p-4 space-y-3">
            <MatchPitchGraphics
              homeTeam={home} awayTeam={away}
              events={events} currentMinute={currentMinute}
              isLive={mode === 'live' && !paused} homeScore={homeScore} awayScore={awayScore}
              matchRatings={matchRatings}
            />
            {/* PAUSE/RESUME + SUBS ROW (below pitch for visibility) */}
            {mode === 'live' && (
              <Button data-testid="sim-modal-fat-pause"
                onClick={togglePause}
                className={`w-full ${paused ? 'bg-[#32D74B] hover:bg-[#32D74B]/90 text-black' : 'bg-[#FFD60A] hover:bg-[#FFD60A]/90 text-black'} font-heading font-black uppercase tracking-wider py-4 text-lg rounded-sm shadow-[0_0_20px_rgba(255,214,10,0.35)]`}>
                {paused ? <><Play size={22} className="mr-2" weight="fill" /> RESUME MATCH</> : <><Pause size={22} className="mr-2" weight="fill" /> PAUSE MATCH</>}
              </Button>
            )}
            {paused && mode === 'live' && (
              <p className="text-center text-[#FFD60A] text-xs animate-pulse">Match paused — make substitutions or formation changes, then resume</p>
            )}
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-6 flex gap-2 flex-wrap justify-start">
                {mode === 'live' && (
                  <Button size="sm" data-testid="modal-home-sub"
                    onClick={() => { setSubTeam(home); setShowSub(true); if (!paused) togglePause(); }}
                    disabled={subsUsed.home >= 5}
                    className="bg-[#007AFF] hover:bg-[#0A84FF] text-white text-xs">
                    <ArrowsLeftRight size={14} className="mr-1" /> Home Sub ({5 - subsUsed.home})
                  </Button>
                )}
              </div>
              <div className="col-span-6 flex gap-2 flex-wrap justify-end">
                {mode === 'live' && (
                  <>
                    <Button size="sm" data-testid="modal-away-sub"
                      onClick={() => { setSubTeam(away); setShowSub(true); if (!paused) togglePause(); }}
                      disabled={subsUsed.away >= 5}
                      className="bg-[#007AFF] hover:bg-[#0A84FF] text-white text-xs">
                      <ArrowsLeftRight size={14} className="mr-1" /> Away Sub ({5 - subsUsed.away})
                    </Button>
                    <Select value={away.formation} onValueChange={(v) => handleFormation(away.id, v)}>
                      <SelectTrigger className="w-[80px] bg-[#121216] border-[#2C2C35] text-white text-xs"><Strategy size={12} /></SelectTrigger>
                      <SelectContent className="bg-[#121216] border-[#2C2C35]">
                        {FORMATIONS.map(f => <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </>
                )}
              </div>
            </div>
            <div className="mt-2">
              <MatchEvents events={events} currentMinute={currentMinute} isLive={mode === 'live'} />
            </div>
            {penInfo && (
              <div className="card-elevated rounded-sm p-3 text-center border border-[#FFD700]">
                <Trophy size={18} className="text-[#FFD700] mx-auto mb-1" weight="fill" />
                <p className="text-sm text-white font-bold">Penalty Shootout: {penInfo.home} - {penInfo.away}</p>
                <p className="text-xs text-[#A0A0A5]">{penInfo.winner === 'home' ? home.name : away.name} win on penalties</p>
              </div>
            )}
            {mode === 'finished' && (
              <Button onClick={onClose} data-testid="sim-modal-close-btn" className="w-full bg-[#32D74B] hover:bg-[#32D74B]/80 text-black font-bold">
                Close & Apply Result
              </Button>
            )}
          </div>
        )}

        {(mode === 'live' || mode === 'finished') && simMode === 'quick' && (
          <div className="p-6 text-center space-y-3">
            <div className="flex items-center justify-center gap-6">
              <span className="font-heading font-bold text-xl text-white">{home.name}</span>
              <span className="score-display text-5xl text-white">{finalResult?.homeScore ?? homeScore}</span>
              <span className="text-2xl text-[#6E6E73]">-</span>
              <span className="score-display text-5xl text-white">{finalResult?.awayScore ?? awayScore}</span>
              <span className="font-heading font-bold text-xl text-white">{away.name}</span>
            </div>
            {finalResult?.pens && (
              <div className="card-elevated rounded-sm p-3 border border-[#FFD700]">
                <Trophy size={18} className="text-[#FFD700] mx-auto mb-1" weight="fill" />
                <p className="text-sm text-white font-bold">Penalty Shootout: {finalResult.pens.home} - {finalResult.pens.away}</p>
                <p className="text-xs text-[#A0A0A5]">{finalResult.pens.winner === 'home' ? home.name : away.name} win on penalties</p>
              </div>
            )}
            {mode === 'finished' && <Button onClick={onClose} className="bg-[#32D74B] hover:bg-[#32D74B]/80 text-black font-bold" data-testid="quick-close-btn">Close & Apply Result</Button>}
          </div>
        )}

        <SubstitutionModal
          isOpen={showSub}
          onClose={() => { setShowSub(false); setSubTeam(null); }}
          team={subTeam}
          onSubstitute={handleSub}
          subsRemaining={subTeam ? (subTeam.id === home.id ? 5 - subsUsed.home : 5 - subsUsed.away) : 5}
        />
      </DialogContent>
    </Dialog>
  );
};
